import React from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import { getHoldings } from 'apis/HoldingAPI';
import { getUserManagementAvailableFeatureFlags } from 'apis/UserAPI';
import Input from 'components/lib/Input';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { useAppSelector } from 'store/hook';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { CompanyType } from 'types/company';
import { HoldingType } from 'types/holding';

import {
  USER_MANAGEMENT_LAST_INVITATION_FILTER_OPTIONS,
  USER_MANAGEMENT_LAST_LOGIN_FILTER_OPTIONS,
} from '../constants';
import UserManagementBulkSelectButton from '../UserManagementBulkSelectButton';
import { useUserManagementContext } from '../UserManagementContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SearchContainer = styled.div`
  width: 500px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const FilterContainer = styled.div`
  display: flex;
`;

const UserManagementFilters = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { companies, positions } = useAppSelector((state) => state.global);

  const { filters, setFilters } = useUserManagementContext();

  const isAdminUser = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);

  const { data: availableFeatureFlags = [] } = useQuery({
    queryKey: ['user-management-feature-flag-options'],
    queryFn: () => getUserManagementAvailableFeatureFlags(),
    enabled: isAdminUser,
    refetchOnWindowFocus: false,
  });

  const { data: holdings = [] } = useQuery({
    queryKey: ['user-management-holdings-filter-options'],
    queryFn: () => getHoldings(),
    enabled: isAdminUser,
    refetchOnWindowFocus: false,
  });

  const companyOptions = (companies || []).map((company: CompanyType) => ({
    label: company.name,
    value: company.id,
  }));

  const holdingOptions = (holdings || []).map((holding: HoldingType) => ({
    label: holding.name,
    value: holding.id,
  }));

  const positionOptions = (positions || []).map((position: any) => ({
    label: position.name,
    value: position.id,
  }));

  const featureFlagOptions = availableFeatureFlags.map((featureFlag: any) => ({
    label: featureFlag.label,
    value: featureFlag.key,
  }));

  const isActiveOptions = [
    { label: 'Active', value: 1 },
    { label: 'Inactive', value: 0 },
  ];

  const handleSearchChange = (event: any) => {
    const search = event.target.value;
    setFilters({ ...filters, search });
  };

  const handleSelectCompany = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, companies: selection.length ? selection : undefined });
  };

  const handleSelectHolding = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, holdings: selection.length ? selection : undefined });
  };

  const handleSelectPosition = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, positions: selection.length ? selection : undefined });
  };

  const handleSelectFeatureFlags = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, feature_flags: selection.length ? selection : undefined });
  };

  const handleSelectIsActive = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, is_active: selection.length ? selection : undefined });
  };

  const handleSelectLastLogin = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, last_login: selection.length ? selection : undefined });
  };

  const handleSelectLastInvitation = (values: SelectAdvancedFilterOptionType[]) => {
    const selection = (values ?? []).map((item: SelectAdvancedFilterOptionType) => item.value);
    setFilters({ ...filters, last_invitation: selection.length ? selection : undefined });
  };

  const searchPlaceholder = 'Search by ID, name, email or company';

  return (
    <Container>
      <SearchContainer>
        <Input
          value={filters?.search}
          prefix={<SearchOutlined />}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
          data-test-id="user-management-search-input"
        />
      </SearchContainer>
      <FiltersRow>
        {isAdminUser && <UserManagementBulkSelectButton />}
        {isAdminUser && (
          <>
            <SelectAdvancedFilter
              className={classNames({ active: filters?.companies && filters.companies.length > 0 })}
              placeholder={'Company'}
              options={companyOptions}
              defaultValue={filters?.companies ?? []}
              onSelect={handleSelectCompany}
              dataTestId="user-management-filter-company"
            />
            <SelectAdvancedFilter
              className={classNames({ active: filters?.holdings && filters.holdings.length > 0 })}
              placeholder={'Owner'}
              options={holdingOptions}
              defaultValue={filters?.holdings ?? []}
              onSelect={handleSelectHolding}
              dataTestId="user-management-filter-owner"
            />
          </>
        )}
        <FilterContainer>
          <SelectAdvancedFilter
            className={classNames({ active: filters?.positions && filters.positions.length > 0 })}
            placeholder={'Position'}
            options={positionOptions}
            defaultValue={filters?.positions ?? []}
            onSelect={handleSelectPosition}
            dataTestId="user-management-filter-position"
          />
        </FilterContainer>
        <FilterContainer>
          <SelectAdvancedFilter
            className={classNames({ active: filters?.is_active && filters.is_active.length > 0 })}
            placeholder={'Status'}
            options={isActiveOptions}
            defaultValue={filters?.is_active ?? []}
            onSelect={handleSelectIsActive}
            dataTestId="user-management-filter-status"
          />
        </FilterContainer>
        <FilterContainer>
          <SelectAdvancedFilter
            className={classNames({ active: filters?.last_login && filters.last_login.length > 0 })}
            placeholder={'Last Login'}
            options={USER_MANAGEMENT_LAST_LOGIN_FILTER_OPTIONS}
            defaultValue={filters?.last_login ?? []}
            onSelect={handleSelectLastLogin}
            dataTestId="user-management-filter-last-login"
          />
        </FilterContainer>
        <FilterContainer>
          <SelectAdvancedFilter
            className={classNames({ active: filters?.last_invitation && filters.last_invitation.length > 0 })}
            placeholder={'Last Invitation'}
            options={USER_MANAGEMENT_LAST_INVITATION_FILTER_OPTIONS}
            defaultValue={filters?.last_invitation ?? []}
            onSelect={handleSelectLastInvitation}
            dataTestId="user-management-filter-last-invitation"
          />
        </FilterContainer>
        {isAdminUser && (
          <FilterContainer>
            <SelectAdvancedFilter
              className={classNames({ active: filters?.feature_flags && filters.feature_flags.length > 0 })}
              placeholder={'Feature Flags'}
              options={featureFlagOptions}
              defaultValue={filters?.feature_flags ?? []}
              onSelect={handleSelectFeatureFlags}
              dataTestId="user-management-filter-feature-flags"
            />
          </FilterContainer>
        )}
      </FiltersRow>
    </Container>
  );
};

export default UserManagementFilters;
